/* eslint-disable @typescript-eslint/no-unused-vars */
import { gql } from '@apollo/client';

export const StudentsQuery = gql`
  query PaginatedStudents($query: String, $first: Int, $after: String) {
    allStudents(query: $query, first: $first, after: $after) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          id
          isLmsBlocked
          user {
            id
            lastName
            firstName
            middleName
            phone
            email
          }
          agreements {
            id
            agreementTitle
            currency
            budgetAmount
            paidAmount
            terminated
            group {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const loadAgreementData = gql`
  query loadAgreementById($agreementId: ID!) {
    agreement(agreementId: $agreementId) {
      id
      agreementTitle
      currency
      terminated
      paymentByCompany
      vat
      date
      comment
      budgetAmount
      paidAmount
      partialAttendance
      terminatedDate
      transferOut
      firstLesson {
        id
        name
        lessonNumber
      }
      lastLesson {
        id
        name
        date
        lessonNumber
      }
      chargeback {
        id
        amountByn
        amountForeign
        currency
      }
      contributions(orderByDateFilter: true) {
        edges {
          node {
            id
            date
            amountForeign
            amountByn
          }
        }
      }
      promocode
      company {
        id
        name
      }
      country {
        id
        title
      }
      resident
      student {
        id
        user {
          id
          firstName
          middleName
          lastName
          phone
          email
        }
      }
      group {
        id
        name
        startDate
        course {
          id
          name
        }
      }
      startAccountsPayable {
        id
        amountByn
        amountForeign
        repaid
      }
      accountsPayable {
        id
        amountForeign
        currency
        startedAgreement {
          id
          agreementTitle
        }
      }
    }
  }
`;

export const MetaStudentsQuery = gql`
  query MetaStudents($query: String) {
    metaStudents(query: $query) {
      totalCount
      agreementsCount
    }
  }
`;

export const UserExistCheck = gql`
  query CheckUserExist($email: String!, $phone: String!) {
    verifyStudent(email: $email, phone: $phone) {
      student {
        id
        user {
          id
          firstName
          lastName
          middleName
          phone
          email
          roles
        }
      }
      user {
        id
        firstName
        lastName
        middleName
        phone
        email
        roles
      }
      customErrors {
        field
        message
        code
      }
      message
      code
    }
  }
`;

export const loadLessonsOptionsForGroup = gql`
  query loadLessonsOptions($groupId: ID!) {
    group(groupId: $groupId) {
      id
      lessons {
        id
        name
        date
        lessonNumber
      }
    }
  }
`;

export const loadAccountsPayables = gql`
  query accountsPayables($id: ID!) {
    student(id: $id) {
      id
      accountsPayables {
        id
        amountForeign
        currency
        repaid
        startedAgreement {
          id
          agreementTitle
        }
      }
    }
  }
`;

/** Stored LMS-access block for a student (null if never blocked). */
export const StudentLmsBlockQuery = gql`
  query StudentLmsBlock($studentId: ID!) {
    studentAccessBlock(studentId: $studentId) {
      id
      isBlocked
      reason
      overdueAmount
      overdueInvoiceCount
      maxOverdueDays
    }
  }
`;

/** Debtor candidates — students with an issued invoice overdue > threshold.
 *  Operation name ≠ backend field name (codegen result-type collision). */
export const DebtorCandidatesQuery = gql`
  query DebtorCandidatesList($groupId: ID) {
    debtorCandidates(groupId: $groupId) {
      student {
        id
        user {
          id
          firstName
          lastName
          middleName
          email
        }
      }
      overdueAmount
      overdueInvoiceCount
      maxOverdueDays
      currentlyBlocked
    }
  }
`;

// --- Notes (spec 0003 Phase 2) ---
export const StudentNotesQuery = gql`
  query StudentNotesList($studentId: ID!) {
    studentNotes(studentId: $studentId) {
      id
      body
      sentiment
      pinned
      createdAt
      authorName
    }
  }
`;

export const GroupNotesQuery = gql`
  query GroupNotesList($groupId: ID!) {
    groupNotes(groupId: $groupId) {
      id
      body
      sentiment
      pinned
      createdAt
      authorName
    }
  }
`;

// --- Tasks (spec 0003 Phase 2 + redesign: student / agreement / group) ---
export const StudentTasksQuery = gql`
  query StudentTasksList($studentId: ID!, $includeClosed: Boolean) {
    studentTasks(studentId: $studentId, includeClosed: $includeClosed) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      assigneeName
      agreement {
        id
        agreementTitle
      }
    }
  }
`;

export const GroupTasksQuery = gql`
  query GroupTasksList($groupId: ID!, $includeClosed: Boolean) {
    groupTasks(groupId: $groupId, includeClosed: $includeClosed) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      assigneeName
      agreement {
        id
        agreementTitle
      }
    }
  }
`;

export const AgreementTasksQuery = gql`
  query AgreementTasksList($agreementId: ID!, $includeClosed: Boolean) {
    agreementTasks(agreementId: $agreementId, includeClosed: $includeClosed) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      assigneeName
      agreement {
        id
        agreementTitle
      }
    }
  }
`;

export const MyTasksQuery = gql`
  query MyTasksList($includeClosed: Boolean) {
    myTasks(includeClosed: $includeClosed) {
      id
      title
      status
      priority
      dueDate
      assigneeName
      group {
        id
        name
      }
      student {
        id
        user {
          id
          firstName
          lastName
        }
      }
      agreement {
        id
        agreementTitle
      }
    }
  }
`;

// Flexible task feed for the command center (filter group/student/agreement/me).
export const StaffTasksQuery = gql`
  query StaffTasksList(
    $groupId: ID
    $studentId: ID
    $agreementId: ID
    $assigneeMe: Boolean
    $dueFrom: Date
    $dueTo: Date
    $search: String
    $includeClosed: Boolean
  ) {
    staffTasks(
      groupId: $groupId
      studentId: $studentId
      agreementId: $agreementId
      assigneeMe: $assigneeMe
      dueFrom: $dueFrom
      dueTo: $dueTo
      search: $search
      includeClosed: $includeClosed
    ) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      updatedAt
      assigneeName
      createdByName
      group {
        id
        name
      }
      student {
        id
        user {
          id
          firstName
          lastName
        }
      }
      agreement {
        id
        agreementTitle
      }
    }
  }
`;

// Active staff (management roles) for assignee pickers. Operation name ≠
// backend field (`staffUsers`) to avoid codegen result-type collision.
export const StaffUsersQuery = gql`
  query StaffUsersList {
    staffUsers {
      id
      name
      email
    }
  }
`;
